import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Key, Plus, Copy, Trash2, Eye, EyeOff, Shield } from 'lucide-react';
import api from '../lib/api';

export function ApiKeyManager() {
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [newKeyName, setNewKeyName] = useState('');
    const [showNewKey, setShowNewKey] = useState(null);
    const [selectedScopes, setSelectedScopes] = useState(['read:products', 'write:orders']);

    const availableScopes = [
        { value: 'read:products', label: 'Read Products', description: 'View product catalog' },
        { value: 'write:orders', label: 'Create Orders', description: 'Create orders from external sources' },
        { value: 'read:orders', label: 'Read Orders', description: 'View order history' },
        { value: 'read:inventory', label: 'Read Inventory', description: 'View stock levels' }
    ];

    useEffect(() => {
        fetchKeys();
    }, []);

    const fetchKeys = async () => {
        try {
            const response = await api.get('/api-keys');
            setKeys(response.data);
        } catch (error) {
            toast.error('Failed to load API keys');
        } finally {
            setLoading(false);
        }
    };

    const createKey = async () => {
        if (!newKeyName.trim()) {
            toast.error('Please enter a name for the API key');
            return;
        }

        setCreating(true);
        try {
            const response = await api.post('/api-keys', {
                name: newKeyName,
                scopes: selectedScopes
            });
            
            setShowNewKey(response.data);
            setNewKeyName('');
            fetchKeys();
            toast.success('API key created! Copy it now - you won\'t see it again.');
        } catch (error) {
            toast.error('Failed to create API key');
        } finally {
            setCreating(false);
        }
    };

    const revokeKey = async (keyId) => {
        if (!confirm('Are you sure? This will immediately revoke access.')) return;
        
        try {
            await api.delete(`/api-keys/${keyId}`);
            fetchKeys();
            toast.success('API key revoked');
        } catch (error) {
            toast.error('Failed to revoke key');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="animate-spin h-8 w-8 border-2 border-cyan-500 border-t-transparent rounded-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Create New Key */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                        <Plus className="h-5 w-5" />
                        Create New API Key
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Key Name</label>
                        <Input
                            placeholder="e.g., DoorDash Integration"
                            value={newKeyName}
                            onChange={(e) => setNewKeyName(e.target.value)}
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium">Permissions</label>
                        <div className="grid grid-cols-2 gap-2">
                            {availableScopes.map((scope) => (
                                <label key={scope.value} className="flex items-start gap-2 p-3 border rounded-lg cursor-pointer hover:bg-muted">
                                    <input
                                        type="checkbox"
                                        checked={selectedScopes.includes(scope.value)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedScopes([...selectedScopes, scope.value]);
                                            } else {
                                                setSelectedScopes(selectedScopes.filter(s => s !== scope.value));
                                            }
                                        }}
                                        className="mt-1"
                                    />
                                    <div>
                                        <p className="font-medium text-sm">{scope.label}</p>
                                        <p className="text-xs text-muted-foreground">{scope.description}</p>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    <Button 
                        onClick={createKey} 
                        disabled={creating || !newKeyName.trim()}
                        className="bg-cyan-500 hover:bg-cyan-600 text-white"
                    >
                        {creating ? 'Creating...' : 'Create API Key'}
                    </Button>
                </CardContent>
            </Card>

            {/* Show Newly Created Key */}
            {showNewKey && (
                <Card className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                            <Shield className="h-5 w-5" />
                            Copy Your API Key Now
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <p className="text-sm text-amber-700 dark:text-amber-400">
                            This is the only time you\'ll see this key. Copy it now and store it securely.
                        </p>
                        <div className="flex gap-2">
                            <code className="flex-1 p-3 bg-black text-green-400 font-mono text-sm rounded-lg break-all">
                                {showNewKey.key}
                            </code>
                            <Button variant="outline" onClick={() => copyToClipboard(showNewKey.key)}>
                                <Copy className="h-4 w-4" />
                            </Button>
                        </div>
                        <Button variant="outline" onClick={() => setShowNewKey(null)} className="w-full">
                            I\'ve Copied It
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Existing Keys */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                        <Key className="h-5 w-5" />
                        Active API Keys ({keys.length})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {keys.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">
                            No API keys yet. Create one to integrate with delivery platforms.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {keys.map((key) => (
                                <div key={key.id} className="flex items-center justify-between p-4 border rounded-lg">
                                    <div className="space-y-1">
                                        <p className="font-medium">{key.name}</p>
                                        <div className="flex gap-1 flex-wrap">
                                            {key.scopes.map((scope) => (
                                                <Badge key={scope} variant="secondary" className="text-xs">
                                                    {scope}
                                                </Badge>
                                            ))}
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            Created: {new Date(key.created_at).toLocaleDateString()}
                                            {key.last_used_at && ` • Last used: ${new Date(key.last_used_at).toLocaleDateString()}`}
                                        </p>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => revokeKey(key.id)}
                                        className="text-destructive hover:text-destructive"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Documentation */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Integration Guide</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                    <p>Use your API key to integrate with delivery platforms:</p>
                    <pre className="bg-muted p-3 rounded-lg overflow-x-auto">
{`POST https://megzebpos-production.up.railway.app/api/external/orders
Headers:
  X-API-Key: mk_live_your_key_here
  Content-Type: application/json

Body:
  {
    "source": "ubereats",
    "external_order_id": "UE-12345",
    "customer": {"name": "John Doe"},
    "items": [...],
    "payment": {...}
  }`}
                    </pre>
                </CardContent>
            </Card>
        </div>
    );
}
